import React, { Fragment, type FC } from 'react'
import clsx from 'clsx'

import { Text } from '@wartek-id/text-v1'

import { TextLabel } from 'app/KinerjaKSPage/components/EvaluationResultV2/components/Texts'
import Grid from 'app/KinerjaKSPage/components/EvaluationResultV2/components/Grid'

type EvaluationResultSummaryProps = React.HTMLAttributes<HTMLDivElement> & {
  period: number
  data: {
    badge?: React.ReactNode
    footnote?: React.ReactNode
    predicates?: Array<{
      label: string
      description: string
      value?: string
    }>
    summary?: {
      label: string
      value?: string
    }
  }
}

/**
 * Renders the evaluation result summary component.
 *
 * @component
 * @param {EvaluationResultSummaryProps} props - The component props.
 * @param {string} props.period - The period of the evaluation result. 1 for Semester 1, 2 for Semester 2, and 3 for Yearly.
 * @param {EvaluationResultData} props.data - The evaluation result data.
 * @returns {JSX.Element} The evaluation result summary component.
 */
const EvaluationResultSummary: FC<EvaluationResultSummaryProps> = ({
  period,
  data,
  ...props
}) => {
  const { badge, predicates, summary } = data

  if (!predicates && !summary) return null

  const PredikatKinerja = () => {
    return (
      <>
        <div className="mt-3 border border-gray-30 rounded-lg">
          {/* Predicates */}
          {!!predicates && (
            <div className="py-4 px-6 space-y-7">
              {predicates?.map((predicate, index) => (
                <Fragment key={index}>
                  <Grid
                    childrenLeft={
                      <>
                        <TextLabel>{predicate.label}</TextLabel>
                        <Text variant="body-sm" color="disabled">
                          {predicate.description}
                        </Text>
                      </>
                    }
                    childrenRight={
                      <TextLabel className="!text-subdued">
                        {predicate?.value}
                      </TextLabel>
                    }
                  />
                </Fragment>
              ))}
            </div>
          )}

          {/* Summary */}
          {!!summary && (
            <div
              className={clsx('py-4 px-6 bg-blue-5 rounded-b-lg', {
                'rounded-t-lg': !predicates,
              })}
            >
              <Grid
                childrenLeft={
                  <Text variant="heading-md" className="flex-1">
                    {summary?.label}
                  </Text>
                }
                childrenRight={
                  <TextLabel className="text-default flex-none">
                    {summary?.value}
                  </TextLabel>
                }
              />
            </div>
          )}
        </div>
      </>
    )
  }

  return (
    <>
      <div className="mt-3" {...props}>
        {(!!predicates || !!summary) && (
          <>
            <div className="flex gap-2">
              <TextLabel>Predikat Kinerja</TextLabel> {!!badge && badge}
            </div>
            <PredikatKinerja />

            {!!data.footnote && data.footnote}
          </>
        )}

        <div className="mt-9 space-y-4">
          {period !== 3 ? (
            <>
              <div>
                <Text variant="body-sm-bold">Perlu diketahui:</Text>
                <ol className="list-decimal ml-4">
                  <Text as="li" variant="body-sm">
                    Dokumen ini diberikan pada bulan Juni dan Desember di Ruang
                    GTK.
                  </Text>
                  <Text as="li" variant="body-sm">
                    Dokumen ini digunakan Kemendikbudristek untuk keperluan
                    analisis capaian dan upaya peningkatan kinerja.
                  </Text>
                  <Text as="li" variant="body-sm">
                    Dokumen ini menjadi salah satu komponen untuk memproses
                    Predikat Kinerja tahunan yang akan dialirkan ke e-Kinerja
                    BKN (Badan Kepegawaian Negara).
                  </Text>
                </ol>
              </div>

              <div>
                <Text variant="body-sm-bold">Catatan:</Text>
                <ol className="list-none">
                  <Text as="li" variant="body-sm">
                    Dokumen ini tidak perlu ditandatangani.
                  </Text>
                </ol>
              </div>
            </>
          ) : (
            <>
              <div>
                <Text variant="body-sm-bold">Catatan:</Text>
                <ol className="list-decimal ml-4">
                  <Text as="li" variant="body-sm">
                    Dokumen ini tidak perlu ditandatangani.
                  </Text>
                  <Text as="li" variant="body-sm">
                    Predikat Kinerja pada dokumen ini akan digunakan untuk
                    konversi Angka Kredit di e-Kinerja BKN (Badan Kepegawaian
                    Negara).
                  </Text>
                </ol>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
export default EvaluationResultSummary
